<template>
  <div id="top" class="provider-registry-page">
    <div class="provider-registry-intro">
      <UiPageHeader title="Providers" description="Search, open, and manage healthcare provider records from one focused register.">
        <template #actions>
          <router-link :to="{ name: 'providerSync' }" class="provider-registry-sync"><i class="material-icons" aria-hidden="true">sync</i><span>Sync</span><span v-if="providerSyncNoticeTotal" class="provider-sync-count">{{ providerSyncNoticeTotal }}</span></router-link>
          <router-link :to="{ name: 'providerAdd' }" class="provider-registry-add"><i class="material-icons" aria-hidden="true">add</i><span>Add provider</span></router-link>
        </template>
      </UiPageHeader>
    </div>
    <div class="progress" v-if="isLoading && isReady" aria-label="Refreshing providers"><div class="indeterminate"></div></div>
    <div id="view"></div>
    <LoadingDiv v-if="!isReady" />
    <template v-else>
      <div class="provider-registry-toolbar">
        <div class="provider-registry-summary" aria-live="polite"><strong>{{ providers.length }} {{ providers.length === 1 ? 'provider' : 'providers' }} in this view</strong><span>Healthcare provider register</span></div>
        <form class="provider-registry-search" @submit.prevent role="search">
          <label for="search">Search providers</label>
          <div class="provider-registry-search-field">
            <i class="material-icons" aria-hidden="true">search</i>
            <input id="search" type="search" v-model="searchItem" placeholder="Search by name or address" autocomplete="off" />
            <button type="button" @click="handleClearSearch" v-if="searchItem" aria-label="Clear provider search"><i class="material-icons" aria-hidden="true">close</i></button>
          </div>
        </form>
      </div>
      <div class="provider-registry-search-status" v-if="searchItem"><span>Search active: <strong>{{ searchItem }}</strong></span><button type="button" @click="handleClearSearch" aria-label="Clear provider search"><i class="material-icons" aria-hidden="true">close</i></button></div>
      <div class="provider-registry-list" v-if="providers.length">
        <router-link class="provider-registry-row" v-for="(provider, index) in providers" :key="provider.id" :to="{ name: 'providerView', params: { id: provider.id } }">
          <span class="provider-registry-number">{{ numberBadge(index) }}</span>
          <div class="provider-registry-main">
            <h2>{{ provider.name }}</h2>
            <p><span>{{ provider.phone || 'No phone recorded' }}</span><span class="provider-contact-dot" aria-hidden="true"></span><span>{{ provider.email || 'No email recorded' }}</span></p>
          </div>
          <span class="provider-registry-state" :class="{ 'is-inactive': provider.status === 's', 'is-pending': provider.status !== 'a' && provider.status !== 's' }">
            <template v-if="provider.status === 'a'">{{ Number(provider.encounters_count || 0).toLocaleString() }} claims</template>
            <template v-else-if="provider.status === 's'">Inactive provider</template>
            <template v-else>Pending approval</template>
          </span>
          <span class="provider-registry-open"><i class="material-icons" aria-hidden="true">arrow_forward</i></span>
        </router-link>
      </div>
      <div class="provider-registry-empty" v-else><h2>No providers found</h2><p>There are no provider records matching this view.</p></div>
      <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-if="providers.length" />
    </template>
  </div>
</template>
<script>
import { computed, onMounted, ref, watch, watchEffect } from "vue";
import useModuleNavigation from "@/composables/useModuleNavigation";
import getItems from "@/composables/getItems";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import { debounce, getCachedPayload, setCachedPayload } from "@/composables/requestOptimization";


export default {
  name: "providerHome",
  components: { UiPageHeader, LoadingDiv, PaginationDiv },
  setup() {
    const syncInc = ref(0);
    const pendingRegistrationInc = ref(0);
    const response = ref(null);
    const isReady = ref(false);
    const isLoading = ref(true);
    const currUrl = ref(null);
    const currHook = ref(null);
    const error = ref(null);
    const searchItem = ref(null);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);
    const providerSearchCacheTtl = 60 * 1000;
    let providerSearchRequestId = 0;

    // vue router
    const { routeToTab } = useModuleNavigation();

    // start
    onMounted(() => {
      // page name
      document.title = "All Providers";
    });

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Providers", title: "All", icon: null, url: "provider", hash: null, });
      tabs.value.push({ id: 2, name: "Providers", title: "View", icon: "visibility", url: "provider", hash: "#view", });
      tabs.value.push({ id: 3, name: "Providers", title: "Add Provider", icon: "add_circle", url: "providerAdd", hash: null, });
      tabs.value.push({ id: 4, name: "Providers", title: "Sync", icon: "sync", url: "providerSync", hash: null, badge: null, });

      // set default
      currTab.value = tabs.value.find(x => x.id === 1);
      // set home
      home.value = tabs.value.find(x => x.id === 1);
    });

    // handle curr status
    const handleActive = (tab) => {
      routeToTab(tab, { replace: tab?.url === "provider" });
    };

    // numbering
    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    // get function
    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // page is ready, loading is complete
        isReady.value = true;
        isLoading.value = false;
      })();
    };

    // starter
    onMounted(() => {
      // get items from function
      (async () => {
        const [readySync, waitingApproval] = await Promise.allSettled([
          getItems("integration/oneprovider/provider-sync/pending/count"),
          getItems("integration/oneprovider/provider-sync/registrations/pending/count"),
        ]);

        if (readySync.status === "fulfilled" && readySync.value.res.value) {
          syncInc.value = Number(readySync.value.res.value.data);
        }

        if (waitingApproval.status === "fulfilled" && waitingApproval.value.res.value) {
          pendingRegistrationInc.value = Number(waitingApproval.value.res.value.data);
        }

      })();
    });

    // starter
    onMounted(() => {
      // set current Url
      currUrl.value = "provider";
      // set current Hook
      currHook.value = "?";
      // get items from function
      getItemsFromApi(currUrl.value);
    });

    // restart
    const handleClearSearch = () => {
      providerSearchRequestId++;
      // start laoding
      isLoading.value = true;
      // null search
      searchItem.value = null;
      // set current Url
      currUrl.value = "provider";
      // set current Hook
      currHook.value = "?";
      // re-get items from function
      getItemsFromApi(currUrl.value);
    };

    const loadProviderSearch = debounce(async (query, requestId) => {
      if (requestId !== providerSearchRequestId || query !== searchItem.value?.trim()) {
        return;
      }

      const url = `provider/list/search?q=${encodeURIComponent(query)}`;
      const cachedPayload = getCachedPayload(url);

      if (cachedPayload) {
        response.value = cachedPayload;
        isReady.value = true;
        isLoading.value = false;
        return;
      }

      const { res, err } = await getItems(url);

      if (requestId !== providerSearchRequestId || query !== searchItem.value?.trim()) {
        return;
      }

      if (res.value) {
        response.value = res.value.data;
        setCachedPayload(url, res.value.data, providerSearchCacheTtl);
      } else {
        error.value = err.value;
      }

      isReady.value = true;
      isLoading.value = false;
    });

    // switch page
    const handlePage = (page) => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      // start laoding
      isReady.value = false;
      // get new page items from function
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`); //
    };

    // search watcher
    watch(searchItem, (value) => {
      const query = value?.trim() || "";
      providerSearchRequestId++;

      if (!query) {
        return;
      }

      if (query.length < 2) {
        isLoading.value = false;
        return;
      }

      // start laoding
      isLoading.value = true;
      // set current Url
      currUrl.value = `provider/list/search?q=${encodeURIComponent(query)}`;
      // set current Hook
      currHook.value = "&";
      // get searchItem items from function
      loadProviderSearch(query, providerSearchRequestId);
    });

    // output results
    const providers = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    const readySyncCount = computed(() => {
      return Number(syncInc.value || 0);
    });

    const awaitingOneProviderCount = computed(() => {
      return Number(pendingRegistrationInc.value || 0);
    });

    const providerSyncNoticeTotal = computed(() => {
      return readySyncCount.value + awaitingOneProviderCount.value;
    });

    watchEffect(() => {
      const syncTab = tabs.value.find(x => x.title === "Sync");

      if (!syncTab) return;

      const badge = providerSyncNoticeTotal.value || null;

      if (syncTab.badge !== badge) {
        syncTab.badge = badge;
      }
    });

    return {
      handleActive,
      home,
      currTab,
      tabs,
      numberBadge,
      handleClearSearch,
      handlePage,
      isReady,
      isLoading,
      error,
      syncInc,
      pendingRegistrationInc,
      searchItem,
      providers,
      allPages,
      readySyncCount,
      awaitingOneProviderCount,
      providerSyncNoticeTotal,
    };
  },
};
</script>

<style scoped>
.provider-registry-page { box-sizing: border-box; margin: 0 auto; max-width: var(--ui-content-width); padding: 2rem 2rem 3rem; width: 100%; }
.provider-registry-intro { border-bottom: 1px solid var(--ui-border); margin-bottom: 2rem; padding-bottom: 1.75rem; }
.provider-registry-intro :deep(.ui-page-header) { margin-bottom: 0; }
.provider-registry-intro :deep(.ui-page-header__title) { font-size: clamp(1.75rem, 2.1vw, 2rem); font-weight: 700; letter-spacing: -.025em; }
.provider-registry-intro :deep(.ui-page-header__description) { font-size: 1rem; line-height: 1.55; }
.provider-registry-add, .provider-registry-sync { align-items: center; border: 1px solid var(--ui-primary); border-radius: calc(var(--ui-radius) - 2px); display: inline-flex; font-size: .8125rem; font-weight: 600; gap: .5rem; min-height: 2.5rem; padding: 0 .9rem; text-decoration: none; }
.provider-registry-add { background: var(--ui-primary); color: var(--ui-primary-foreground); }
.provider-registry-sync { background: var(--ui-background); border-color: var(--ui-border); color: var(--ui-foreground); }
.provider-registry-intro i { font-size: 1rem; }
.provider-sync-count { background: var(--ui-secondary); border-radius: 999px; font-size: .6875rem; padding: .1rem .4rem; }
.provider-registry-toolbar { align-items: center; border-bottom: 1px solid var(--ui-border); display: flex; gap: 1.5rem; justify-content: space-between; padding-bottom: 1.25rem; }
.provider-registry-summary { display: flex; flex-direction: column; gap: .15rem; }
.provider-registry-summary strong { color: var(--ui-foreground); font-size: 1rem; font-weight: 650; }
.provider-registry-summary > span { color: var(--ui-muted-foreground); font-size: .8125rem; }
.provider-registry-search { width: min(100%, 24rem); }
.provider-registry-search > label { display: block; font-size: .75rem; margin-bottom: .4rem; }
.provider-registry-search-field { align-items: center; display: flex; position: relative; }
.provider-registry-search-field > i { color: var(--ui-muted-foreground); font-size: 1.1rem; left: .75rem; position: absolute; }
.provider-registry-search-field input { box-sizing: border-box; padding-left: 2.25rem !important; padding-right: 2.5rem !important; }
.provider-registry-search-field button { align-items: center; background: transparent; border: 0; color: var(--ui-muted-foreground); cursor: pointer; display: flex; padding: .35rem; position: absolute; right: .5rem; }
.provider-registry-search-field button i { font-size: 1rem; }
.provider-registry-search-status { align-items: center; border-bottom: 1px solid var(--ui-border); color: var(--ui-muted-foreground); display: flex; font-size: .8125rem; gap: 1rem; justify-content: space-between; padding: .75rem 0; }
.provider-registry-search-status button { background: transparent; border: 0; color: inherit; cursor: pointer; }
.provider-registry-search-status i { font-size: 1rem; }
.provider-registry-row { align-items: center; border-bottom: 1px solid var(--ui-border); color: var(--ui-foreground); display: grid; gap: 1.25rem; grid-template-columns: 2.5rem minmax(0, 1fr) minmax(10rem, auto) 2.5rem; min-height: 5rem; padding: 1.15rem .75rem; text-decoration: none; transition: background-color 160ms ease; }
.provider-registry-row:is(:hover, :focus-visible) { background: color-mix(in srgb, var(--ui-muted) 45%, transparent); }
.provider-registry-number { color: var(--ui-muted-foreground); font-size: .8125rem; font-weight: 500; }
.provider-registry-main { min-width: 0; }
.provider-registry-main h2 { color: var(--ui-foreground); font-size: .875rem; font-weight: 600; line-height: 1.35; margin: 0 0 .35rem; }
.provider-registry-main p { align-items: center; color: var(--ui-muted-foreground); display: flex; flex-wrap: wrap; font-size: .75rem; gap: 7px; margin: 0; overflow-wrap: anywhere; }
.provider-contact-dot { background: var(--ui-border); border-radius: 50%; height: 5px; width: 5px; }
.provider-registry-state { background: var(--ui-secondary); border: 1px solid var(--ui-border); border-radius: 999px; color: var(--ui-muted-foreground); font-size: .6875rem; font-weight: 500; justify-self: end; padding: .25rem .6rem; white-space: nowrap; }
.provider-registry-state.is-inactive { background: #fff1f2; border-color: #fecdd3; color: #be123c; }
.provider-registry-state.is-pending { background: #fffbeb; border-color: #fde68a; color: #92400e; }
.provider-registry-open { align-items: center; background: var(--ui-background); border: 1px solid var(--ui-border); border-radius: 6px; color: var(--ui-muted-foreground); display: inline-flex; height: 36px; justify-content: center; justify-self: end; width: 36px; }
.provider-registry-open i { font-size: 18px; text-decoration: none; }
.provider-registry-row:is(:hover, :focus-visible) .provider-registry-open { background: var(--ui-foreground); border-color: var(--ui-foreground); color: var(--ui-background); }
.provider-registry-page :is(a, button):focus-visible { outline: 2px solid var(--ui-ring); outline-offset: 2px; }
.provider-registry-empty { color: var(--ui-muted-foreground); padding: 2rem 0; text-align: center; }
.provider-registry-empty h2 { color: var(--ui-foreground); font-size: 1rem; font-weight: 600; }
.provider-registry-empty p { font-size: .8125rem; }
@media (max-width: 700px) {
  .provider-registry-page { padding: 1.25rem 1rem 2rem; }
  .provider-registry-toolbar { align-items: stretch; flex-direction: column; }
  .provider-registry-search { width: 100%; }
  .provider-registry-row { gap: .75rem; grid-template-columns: 1.5rem minmax(0, 1fr) 2.5rem; }
  .provider-registry-state { grid-column: 2; justify-self: start; }
  .provider-registry-open { grid-column: 3; grid-row: 1 / 3; }
}
.provider-page-surface {
  min-height: calc(100vh - 260px);
  padding-bottom: 56px;
  padding-top: 0;
}

.provider-client-surface,
.provider-client-surface.section {
  margin-top: -14px;
  padding-bottom: 0;
  padding-top: 0;
  position: relative;
  z-index: 1;
}

.provider-review-head,
.provider-search-card,
.provider-search-status,
.provider-client-card,
.provider-empty-state {
  box-sizing: border-box;
  width: 100%;
}

.provider-review-head,
.provider-search-card,
.provider-search-status,
.provider-client-card,
.provider-empty-state,
.provider-page-surface :deep(.pagination) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.provider-review-head {
  align-items: flex-end;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px 6px 0 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  display: flex;
  gap: 22px;
  justify-content: space-between;
  margin-bottom: 0;
  overflow: visible;
  padding: 24px 26px;
  position: relative;
  z-index: 2;
}

.provider-review-head::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 18px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.provider-title-block {
  position: relative;
  z-index: 2;
}

.provider-title-block h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.provider-title-block p {
  color: #607d8b;
  line-height: 1.45;
  margin: 6px 0 0;
  max-width: 720px;
}

.provider-add-link {
  align-items: center;
  background: #ffffff;
  border: 1px solid #80cbc4;
  border-radius: 999px;
  color: #00695c;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 8px;
  min-height: 40px;
  padding: 0 13px 0 16px;
  position: relative;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease, transform 0.18s ease;
  z-index: 2;
}

.provider-add-link:hover {
  background: #e0f2f1;
  border-color: #00897b;
  color: #004d40;
  transform: translateY(-1px);
}

.provider-add-link i {
  color: #00897b;
  font-size: 19px;
}

.provider-search-card {
  background: #f8fafb;
  border: 1px solid #c4d3d9;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.78);
  margin-bottom: 16px;
  padding: 12px 16px;
}

.provider-search-card:has(+ .provider-search-status) {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: none;
  margin-bottom: 0;
}

.provider-search-card form,
.provider-search-card .input-field {
  margin: 0;
}

.provider-search-card .input-field {
  position: relative;
}

.provider-search-card input[type="search"] {
  background: #ffffff;
  border: 1px solid #d5e1e5 !important;
  border-radius: 6px;
  box-sizing: border-box;
  box-shadow: none !important;
  color: #263238;
  font-weight: 600;
  height: 48px;
  line-height: 48px;
  margin: 0;
  padding-left: 46px;
  padding-right: 42px;
  width: 100%;
}

.provider-search-card input[type="search"]:focus {
  border-color: #80cbc4 !important;
  box-shadow: 0 0 0 3px rgba(128, 203, 196, 0.18) !important;
}

.provider-search-card .label-icon {
  align-items: center;
  display: flex;
  height: 48px;
  left: 10px;
  line-height: 48px;
  position: absolute;
  top: 0;
}

.provider-search-card .material-icons {
  color: #00897b;
  height: 48px;
  line-height: 48px;
}

.provider-search-card .input-field > i {
  align-items: center;
  cursor: pointer;
  display: flex;
  height: 48px;
  line-height: 48px;
  position: absolute;
  right: 10px;
  top: 0;
}

.provider-search-status {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  box-shadow: 0 16px 28px -24px rgba(38, 50, 56, 0.72);
  color: #263238;
  display: flex;
  justify-content: space-between;
  margin: 0 0 16px;
  padding: 12px 14px 12px 16px;
}

.provider-search-status span {
  color: #607d8b;
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.provider-search-status strong {
  color: #263238;
  font-size: 0.98rem;
}

.provider-search-status button {
  align-items: center;
  background: #f7f7f7;
  border: 1px solid #e1e8eb;
  border-radius: 50%;
  color: #455a64;
  cursor: pointer;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  padding: 0;
  width: 32px;
}

.provider-search-status button i {
  font-size: 18px;
}

.provider-client-card {
  background: linear-gradient(180deg, #ffffff 0%, #fdfdfd 100%);
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 18px 32px -30px rgba(38, 50, 56, 0.72) !important;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.provider-client-card:hover {
  border-color: #80cbc4;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 18px 32px -28px rgba(0, 105, 92, 0.34) !important;
  transform: translateY(-1px);
}

.provider-client-card .card-content {
  background: transparent;
}

.provider-client-card h5 {
  color: #263238;
  font-size: 1.22rem;
  font-weight: 700;
  line-height: 1.35;
  margin-top: 0;
}

.provider-client-card h5 a {
  color: #263238;
}

.provider-client-card h5 a:hover {
  color: #00897b;
}

.provider-client-card p {
  color: #455a64;
  margin-bottom: 0;
}

.provider-empty-state {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 16px 28px -28px rgba(38, 50, 56, 0.72);
  color: #607d8b;
  margin-top: 16px;
  padding: 34px 20px;
}

.provider-empty-state i {
  align-items: center;
  background: #e0f2f1;
  border: 1px solid #80cbc4;
  border-radius: 50%;
  color: #00897b;
  display: inline-flex;
  font-size: 24px;
  height: 48px;
  justify-content: center;
  margin-bottom: 10px;
  width: 48px;
}

.provider-empty-state h6 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 6px;
}

.provider-empty-state p {
  margin: 0;
}

@media only screen and (max-width: 992px) {
  .provider-review-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .provider-add-link {
    min-height: 42px;
  }
}

@media only screen and (max-width: 600px) {
  .provider-review-head {
    padding: 22px;
  }

  .provider-title-block h5 {
    font-size: 1.45rem;
  }

  .provider-title-block p {
    font-size: 0.94rem;
  }

  .provider-add-link {
    border-radius: 8px;
    justify-content: center;
    width: 100%;
  }

  .provider-search-card {
    padding: 10px;
  }

  .provider-search-status {
    align-items: flex-start;
    gap: 12px;
  }

  .provider-client-card {
    border-radius: 8px;
  }

  .provider-client-card .card-content {
    padding: 18px;
  }

  .provider-client-card h5 {
    display: flex;
    flex-direction: column;
    font-size: 1rem;
    gap: 8px;
  }

  .provider-client-card h5 .badge {
    align-self: flex-start;
    float: none;
    margin-left: 0;
    position: static;
  }

  .provider-client-card p {
    display: flex;
    flex-direction: column;
    font-size: 0.88rem;
    gap: 4px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .provider-review-head,
  .provider-search-card,
  .provider-search-status,
  .provider-client-card,
  .provider-empty-state,
  .provider-page-surface :deep(.pagination) {
    animation: none;
  }
}
</style>
